import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { useTranslation } from 'react-i18next'

import { snakeCaseToPascalCase } from '../../../../utils/helpers'
import MediaUpload from '../Media/MediaUpload'
import { augmentIncomingCreativeMedia } from '../Media/mediaPayload'
import { AssetClass, getLocaleForLanguage } from 'api/models'
import { fetchAudiences } from 'api/integrations'
import { arrayReducer, initStateArray, objectReducer, initStateObject } from 'reducers/default'
import { getUserReports } from 'api/userReports'
import Targeting from '../Edit/RunningCampaign/Targeting'
import { Keywords } from '../Edit/CampaignForm/Keywords'
import LanguageTabs from '../Edit/CampaignForm/LanguageTabs'
import CarouselAd from '../CarouselAd'
import TabSwitch from 'components/Shared/TabSwitch'


export default function RunningCampaign({
  eventId,
  tc,
  tc_run_id,
  keywords,
  creative,
  targeting,
  integrationDetails,
  tasteCluster,
  audienceId,
  savedAudienceId,
}) {
  const { t } = useTranslation()
  const [audiences, dispatchAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [savedAudiences, dispatchSavedAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [userReports, userReportsDispatch] = useReducer(objectReducer, initStateObject)

  const [activeLanguage, setActiveLanguage] = useState(creative.language || 'null')
  const [chosenLanguages, setChosenLanguages] = useState([])

  const hasCarouselCards = useMemo(() => {
    return creative.carousel_cards && creative.carousel_cards.length > 0
  }, [creative])

  const runningCarouselCards = useMemo(() => {
    if (!Array.isArray(creative.carousel_cards)) return []

    return creative.carousel_cards.map((card, index) => ({
      ...card,
      id: card.id || `running-carousel-${index}`,
    }))
  }, [creative.carousel_cards])

  const handleActiveLanguageChange = useCallback((newLanguage) => {
    setActiveLanguage(newLanguage)
  }, [])

  // Get content for active language
  const getCurrentLanguageContent = useCallback(() => {
    const firstLanguage = chosenLanguages[0]

    // if language === null return main fields (for past campaigns)
    if (creative.language === null && activeLanguage === 'null') {
      return {
        body: creative.body || [],
        headline: creative.headline || [],
        description: creative.description || ''
      }
    }
      
    // If it's the first language, return main fields
    if (firstLanguage && activeLanguage === firstLanguage.value) {
      return {
        body: creative.body || [],
        headline: creative.headline || [],
        description: creative.description || ''
      }
    }
      
    // For other languages, get from localizations
    const locale = getLocaleForLanguage(activeLanguage)
    const localization = creative.localizations?.[locale] || {
      bodies: [],
      headlines: [],
      description: ''
    }
      
    return {
      body: localization.bodies || [],
      headline: localization.headlines || [],
      description: localization.description || ''
    }
  }, [activeLanguage, chosenLanguages, creative])

  const retrieveAudiences = useCallback(() => {
    const adAccountId = (integrationDetails || []).find((x) => x.asset_class === AssetClass.ad_account)?.asset_id
    if (adAccountId) {
      fetchAudiences(dispatchAudiencesAction, adAccountId, 'custom')
      fetchAudiences(dispatchSavedAudiencesAction, adAccountId, 'saved')
    }
  }, [integrationDetails])

  const audienceNames = useMemo(() => {
    const names = []
    
    // add custom audiences
    if (audienceId) {
      let audienceIds = audienceId.split(',')
      audienceIds.forEach((audience_id) => {
        const audience = audiences.items.length && audiences.items.find((audience) => audience.id === audience_id)
        if (audience) {
          names.push(audience.name)
        }
      })
    }
    
    // add saved audience
    if (savedAudienceId) {
      const savedAudience = savedAudiences.items.length && savedAudiences.items.find((audience) => audience.id === savedAudienceId)
      if (savedAudience) {
        names.push(savedAudience.name)
      }
    }
    
    return names
  }, [audiences, savedAudiences, audienceId, savedAudienceId])

  useEffect(() => {
    retrieveAudiences()
  }, [retrieveAudiences])

  useEffect(() => {
    getUserReports(userReportsDispatch, eventId, {
      reportType: 'WRONG_TOPIC_RECOMMENDATION',
      tc,
      tc_run_id,
    })
  }, [eventId, tc, tc_run_id])

  const reportedKeywords = useMemo(() => {
    if (userReports.content?.items) {
      return userReports.content.items.map((item) => ({
        keyword: item.keyword,
        id: item.id,
      }))
    }
    return []
  }, [userReports])

  useEffect(() => {
    const languagesFromCreative = []
    
    // Add main language
    if (creative.language) {
      languagesFromCreative.push({
        text: `common.language.${creative.language}`,
        value: creative.language
      })
    }
    
    // Add languages from localizations sorted by index
    if (creative.localizations) {
      const sortedEntries = Object.entries(creative.localizations)
        .sort(([,a], [,b]) => (a.index || 0) - (b.index || 0))
      
      sortedEntries.forEach(([locale]) => {
        const langCode = locale.split('_')[0]
        if (!languagesFromCreative.some(lang => lang.value === langCode)) {
          languagesFromCreative.push({
            text: `common.language.${langCode}`,
            value: langCode
          })
        }
      })
    }
    
    setChosenLanguages(languagesFromCreative)
  }, [creative])

  if (!creative) return null

  return (
    <div className="recommendation__details__running">
      <Keywords
        campaign={{ tc, tc_run_id }}
        eventId={eventId}
        isCampaignRunning
        keywords={keywords}
        reportedKeywords={reportedKeywords}
        className="mb-4 pb-3"
        reportsEnabled={false}
      />

      
      <p className="recommendation__details__running__section__title">
        {t('Recommendations.campaign.published.media')}
      </p>

      <TabSwitch 
        tabSwitch={hasCarouselCards ? 'carousel' : 'single'}
        setTabSwitch={() => {}} 
        options={['single', 'carousel']} 
        classNames="mb-3"
        style={{ pointerEvents: 'none', cursor: 'default' }}
        translationKey="Recommendations.campaign.setup.mediaSwitch"
      />

      {hasCarouselCards 
        ? 
        <div className="mb-4">
          <CarouselAd cards={runningCarouselCards} readonly={true} language={creative.language}/> 
        </div>
        : Array.isArray(creative.media) ? (
          <div className="recommendation__details__running__section row recommendation__details__running__media">
            <div className="col-12">
              <MediaUpload 
                onChange={() => {}} 
                onError={() => {}} 
                initialMedia={augmentIncomingCreativeMedia(creative)}
                readonly />
            </div>
          </div>
        ) : null}

      {creative.language && !hasCarouselCards && 
        <LanguageTabs
          activeLanguage={activeLanguage}
          chosenLanguages={chosenLanguages}
          onLanguageChange={handleActiveLanguageChange}
          mode = 'running'
        />
      }

      <div className="recommendation__details__running__section--accent">
        {(() => {
          const currentContent = getCurrentLanguageContent()
          
          return (
            <>
              {currentContent.body?.length > 0 ? (
                <div className="recommendation__details__running__section">
                  <p className="recommendation__details__running__section__title">
                    {t('Recommendations.campaign.published.body')}
                  </p>

                  {currentContent.body.map((body, index) => (
                    <p key={index} className="recommendation__details__running__section__body">
                      {body}
                    </p>
                  ))}
                </div>
              ) : null}

              {currentContent.headline?.length > 0 ? (
                <div className="recommendation__details__running__section">
                  <p className="recommendation__details__running__section__title">
                    {t('Recommendations.campaign.published.headline')}
                  </p>

                  {currentContent.headline.map((headline, index) => (
                    <p key={index} className="recommendation__details__running__section__body">
                      {headline}
                    </p>
                  ))}
                </div>
              ) : null}     
            
              {currentContent.description ? (
                <div className="recommendation__details__running__section mb-0">
                  <p className="recommendation__details__running__section__title">
                    {t('Recommendations.campaign.published.description')}
                  </p>

                  <p className="recommendation__details__running__section__body">{currentContent.description}</p>
                </div>
              ) : null}
            </>
          )
        })()}
      </div>

      {creative.destination_url ? (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.destinationUrl')}
          </p>
          {creative.destination_url.map((destination_url, index) => (
            <p key={index} className="recommendation__details__running__section__body">
              {destination_url}
            </p>
          ))}
        </div>
      ) : null}

      {creative.call_to_action ? (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.callToAction')}
          </p>
          <p className="recommendation__details__running__section__body">
            {t([
              `Recommendations.campaign.setup.call_to_action.${creative.call_to_action}`,
              snakeCaseToPascalCase(creative.call_to_action),
            ])}
          </p>
        </div>
      ) : null}

      {audienceNames.length > 0 ? (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.audience')}
          </p>
          {audienceNames.map((name, index) => {
            return (
              <p key={index} className="recommendation__details__running__section__body">
                {name}
              </p>
            )
          })}
        </div>
      ) : (
        <div className="recommendation__details__running__section">
          <p className="recommendation__details__running__section__title">
            {t('Recommendations.campaign.published.audience')}
          </p>
          
          <p className="recommendation__details__running__section__body">{tasteCluster}</p>
        </div>
      )}

      <Targeting tc={tc} tc_run_id={tc_run_id} targeting={targeting} />


    </div>
  )
}

RunningCampaign.propTypes = {
  eventId: PropTypes.string.isRequired,
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  keywords: PropTypes.array.isRequired,
  creative: PropTypes.object.isRequired,
  targeting: PropTypes.array.isRequired,
  integrationDetails: PropTypes.arrayOf(object),
  tasteCluster: PropTypes.string,
  audienceId: PropTypes.string,
  savedAudienceId: PropTypes.string,
}
